/**
 * Frontend route protection (v10 rev 5, step 04).
 *
 * FINDING THIS ADDRESSES
 *   `apps/web/src/routes` contains 94 route files and ZERO guards: no
 *   `beforeLoad`, no `loader`, no `isAuthenticated` check. Typing the URL of
 *   any clinical screen rendered it for anyone.
 *
 * HONEST LIMIT
 *   A client-side guard is USER EXPERIENCE, not a security boundary. It stops
 *   a clinician from wandering into a screen they cannot use; it does not
 *   stop anyone, because the data is fetched separately and the API is the
 *   real boundary. The server-side deny-by-default guard added in
 *   `apps/api/src/middleware/auth-guard.ts` is what actually protects data.
 *
 *   Both are required: the API stops the breach, this stops the confusion.
 */
import type { AuthUser } from "./tenant-context";

/** Routes reachable without a session. Keep this list minimal. */
const PUBLIC_ROUTES: ReadonlyArray<string> = ["/login", "/auth", "/404", "/public"];

export function isPublicRoute(pathname: string): boolean {
  const p = (pathname || "/").split("?")[0];
  return PUBLIC_ROUTES.some((r) => p === r || p.startsWith(`${r}/`));
}

export interface SessionState {
  user: AuthUser | null;
  /** Token is kept in an HTTP-only cookie by the server; this is display state only. */
  authenticated: boolean;
}

/**
 * Read the current session.
 *
 * The access token is NOT read from localStorage — the rev-1 auth-bridge
 * mirrored it there, which is why it had to be removed from storage in the
 * first place. Anything still present in localStorage under the legacy key is
 * ignored here on purpose.
 */
export function readSession(): SessionState {
  if (typeof window === "undefined") return { user: null, authenticated: false };
  try {
    const raw = window.localStorage.getItem("dikdoc_user");
    if (!raw) return { user: null, authenticated: false };
    const user = JSON.parse(raw) as AuthUser;
    return { user, authenticated: Boolean(user && user.id) };
  } catch {
    return { user: null, authenticated: false };
  }
}

/** Coarse role buckets used for navigation-level gating. */
export type AppRole = "admin" | "clinician" | "lab" | "radiology" | "pharmacy" | "billing";

export function rolesOf(user: AuthUser | null): AppRole[] {
  if (!user) return [];
  const raw = (user.roles ?? []) as string[];
  const out: AppRole[] = [];
  for (const r of raw) {
    const k = r.toLowerCase();
    if (k === "admin" || k.includes("admin")) out.push("admin");
    if (k.includes("doctor") || k.includes("nurse") || k.includes("clinician") || k.includes("practitioner")) out.push("clinician");
    if (k.includes("lab") || k.includes("biolog")) out.push("lab");
    if (k.includes("radiolog") || k.includes("imaging")) out.push("radiology");
    if (k.includes("pharmac")) out.push("pharmacy");
    if (k.includes("billing") || k.includes("facturation")) out.push("billing");
  }
  return [...new Set(out)];
}

/** Route prefixes that require a specific role bucket. */
const ROLE_GATED: ReadonlyArray<{ prefix: string; roles: AppRole[] }> = [
  { prefix: "/admin", roles: ["admin"] },
  { prefix: "/integration", roles: ["admin"] },
  { prefix: "/settings", roles: ["admin"] },
  { prefix: "/lab-results", roles: ["clinician", "lab", "admin"] },
  { prefix: "/radiology", roles: ["clinician", "radiology", "admin"] },
  { prefix: "/catalogs", roles: ["clinician", "pharmacy", "lab", "admin"] },
  { prefix: "/referential", roles: ["clinician", "lab", "radiology", "pharmacy", "billing", "admin"] },
];

export type GuardDecision =
  | { allowed: true }
  | { allowed: false; reason: "unauthenticated" | "insufficient-role"; redirect: string };

/**
 * Decide whether the current user may enter a path.
 * Pure function so it can be unit-tested without a DOM.
 */
export function evaluateRoute(
  pathname: string,
  state: SessionState,
): GuardDecision {
  const p = (pathname || "/").split("?")[0];
  if (isPublicRoute(p)) return { allowed: true };
  if (!state.authenticated) return { allowed: false, reason: "unauthenticated", redirect: "/login" };

  const gate = ROLE_GATED.find((g) => p === g.prefix || p.startsWith(`${g.prefix}/`));
  if (!gate) return { allowed: true };

  const mine = rolesOf(state.user);
  // No role information at all must not be treated as "admin".
  if (mine.length === 0) {
    return { allowed: false, reason: "insufficient-role", redirect: "/login" };
  }
  if (mine.includes("admin")) return { allowed: true };
  if (gate.roles.some((r) => mine.includes(r))) return { allowed: true };
  return { allowed: false, reason: "insufficient-role", redirect: "/" };
}